/**
 * App Configuration
 *
 * Central configuration for the application.
 * This file serves as the source of truth for app identifiers and other
 * environment-specific values.
 */

import { Capacitor } from '@capacitor/core';
import type { PassedInitialConfig, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { LogLevel } from 'angular-auth-oidc-client';

import { APP_CONFIG_REGISTRY } from './app/config/app-config.registry';
import { environment } from './environments/environment';
import type { BackendConfig, Environment } from './environments/environment.model';

const BASE_APP_ID = environment.appId;

function getCallbackUrl(): string {
  if (Capacitor.isNativePlatform()) {
    return `${BASE_APP_ID}://callback/`;
  }
  if (environment.environment === 'dev') {
    return `${globalThis.location.origin}/auth-redirect.html`;
  }
  return globalThis.location.origin;
}

function createOidcConfig(configId: string, issuer: string, clientId: string, secureRoutes: Array<string>): OpenIdConfiguration {
  return {
    configId,
    authority: issuer,
    redirectUrl: getCallbackUrl(),
    postLogoutRedirectUri: getCallbackUrl(),
    clientId,
    scope: `${clientId} openid offline_access`,
    responseType: 'code',
    silentRenew: true,
    useRefreshToken: true,
    renewTimeBeforeTokenExpiresInSeconds: 60,
    allowUnsafeReuseRefreshToken: true,
    customParamsRefreshTokenRequest: {
      redirect_uri: getCallbackUrl(),
    },
    issValidationOff: true,
    strictIssuerValidationOnWellKnownRetrievalOff: true,
    disableIdTokenValidation: false,
    disableIatOffsetValidation: true,
    disableRefreshIdTokenAuthTimeValidation: true,
    ignoreNonceAfterRefresh: true,
    unauthorizedRoute: '/login',
    postLoginRoute: '',
    //    logLevel: LogLevel.Debug,
    logLevel: LogLevel.None,
    customParamsAuthRequest: {
      prompt: 'login',
      response_mode: 'query',
    },
    autoUserInfo: false,
    secureRoutes,
  };
}

/**
 * Collect the backend URLs a config protects with its token.
 *
 * Empty entries are dropped: an endpoint that is not rolled out yet is kept as
 * '' in the backend config, and ClosestMatchingRouteService matches secure
 * routes with `route.startsWith(configuredRoute)`. Since every URL starts with
 * '', a single empty entry makes its config match every request — including
 * requests belonging to another backend, whose token then is not found and the
 * Authorization header is dropped without an error.
 */
function collectSecureRoutes(backend: BackendConfig): Array<string> {
  return Object.values(backend).filter(url => url.length > 0);
}

/**
 * Build OIDC configs for all available backends, sorted so the active config
 * comes first.
 *
 * The OIDC library's ClosestMatchingRouteService iterates loaded configs by
 * insertion order and picks the first matching one. By sorting so the active
 * config (matching environment.environment) is first, shared URLs like
 * deviceApiUrl resolve to the correct config and its tokens. Unique URLs
 * are only present in one config anyway, so insertion order doesn't matter.
 */
function buildActiveOidcConfigs(): Array<OpenIdConfiguration> {
  const allEntries = APP_CONFIG_REGISTRY.filter(entry => entry.availableIn.includes(environment.environment));

  // Sort: active config first, then others by their registry order
  const activeKey = environment.environment;
  const sortedEntries = [...allEntries].sort((a, b) => {
    if (a.key === activeKey) return -1;
    if (b.key === activeKey) return 1;
    return 0;
  });

  return sortedEntries.map(entry =>
    createOidcConfig(entry.key, entry.config.auth.issuer, entry.config.auth.clientId, collectSecureRoutes(entry.config.backend))
  );
}

export const APP_CONFIG = {
  baseAppId: BASE_APP_ID,

  authorization: {
    config: buildActiveOidcConfigs(),
  } as PassedInitialConfig,
};
